import { useState, type FormEvent } from "react";
import { describeError } from "@/api/client";
import { useSession } from "@/api/session";
import type { Uuid } from "@/api/types";
import {
  AlertDialog,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  InlineAlert,
  Quantity,
  TextField,
  useToast,
} from "@/components";
import type { DecimalString } from "@/lib/decimal";
import { formatGrams, formatNumber, pluralize, todayIso } from "@/lib/format";
import { useCancelLot, useReceiveLot, useReceiveOrder, type Lot, type LotDetail } from "../api";
import { dilutionText } from "../lotDisplay";
import { receivingTolerance, receivingWeightInput, weighIn } from "../weighIn";
import { LocationSelect } from "./LocationSelect";
import { WeightInput } from "./WeightInput";
import styles from "./ReceiveDialogs.module.css";

type OnOrderLot = Pick<Lot, "id" | "display_code" | "material" | "labelled_qty_g" | "dilution_label">;

interface DialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** One container arrives: the date, an optional weigh-in against the label, where it goes. */
export function ReceiveLotDialog({
  lot,
  open,
  onOpenChange,
  onReceived,
}: DialogBaseProps & { lot: OnOrderLot; onReceived?: (lot: LotDetail) => void }) {
  const session = useSession();
  const receive = useReceiveLot();
  const toast = useToast();
  const [receivedAt, setReceivedAt] = useState(todayIso);
  const [gross, setGross] = useState<DecimalString | null>(null);
  const [tare, setTare] = useState<DecimalString | null>(null);
  const [bookWeighed, setBookWeighed] = useState(true);
  const [locationId, setLocationId] = useState<Uuid | null>(null);
  const [supplierLot, setSupplierLot] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [errors, setErrors] = useState<{ gross?: string; tare?: string; form?: string }>({});
  const check = weighIn(gross, tare, lot.labelled_qty_g, receivingTolerance(session.settings));
  const weights = receivingWeightInput(gross, tare, lot.labelled_qty_g);

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (Object.keys(weights.errors).length) {
      setErrors({ ...weights.errors, form: weights.errors.labelled });
      return;
    }
    receive.mutate(
      {
        lotId: lot.id,
        received_at: receivedAt || null,
        gross_weight_g: gross,
        container_tare_g: tare,
        record_correction: bookWeighed,
        location_id: locationId,
        supplier_lot_number: supplierLot.trim() || null,
        expires_at: expiresAt || null,
      },
      {
        onSuccess: (received) => {
          toast.success(
            received.underweight ? `Lot ${lot.display_code} received underweight` : `Lot ${lot.display_code} received`,
            { description: `${lot.material.name}, ${formatGrams(received.balance_g)} g` },
          );
          onReceived?.(received);
          onOpenChange(false);
        },
        onError: (failure) => setErrors({ form: describeError(failure) }),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Receive lot ${lot.display_code}`}
      description={`${lot.material.name}, ${dilutionText(lot)}${lot.labelled_qty_g ? `, ${formatNumber(lot.labelled_qty_g, { maxDecimals: 3 })} g on the label` : ""}.`}
      footer={
        <>
          <DialogClose />
          <Button variant="primary" icon="check" onClick={() => submit()} pending={receive.isPending}>
            Receive lot
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className={styles.form}>
        <TextField label="Received" type="date" value={receivedAt} onValueChange={setReceivedAt} />
        <div className={styles.weights}>
          <WeightInput
            label="Gross weight"
            optional
            value={gross}
            onValueChange={(value) => {
              setGross(value);
              setErrors({});
            }}
            error={errors.gross}
          />
          <WeightInput
            label="Container tare"
            optional
            description="Leave blank to estimate from gross weight minus the labelled quantity."
            value={tare}
            onValueChange={(value) => {
              setTare(value);
              setErrors({});
            }}
            error={errors.tare}
          />
        </div>
        {check ? (
          <p className={styles.check} aria-live="polite">
            Net <Quantity value={check.net.toString()} unit="g" />
            {check.difference !== null ? (
              check.underweight ? (
                <Badge tone="warning" icon="alertTriangle">
                  Underweight by {formatGrams(check.difference.abs().toString())} g
                </Badge>
              ) : (
                <Badge tone="success" icon="check">
                  Matches the label
                </Badge>
              )
            ) : null}
          </p>
        ) : weights.estimatedTare !== null ? (
          <p className={styles.hint} aria-live="polite">
            Estimated container tare: <Quantity value={weights.estimatedTare.toString()} unit="g" />. The labelled
            quantity will be booked.
          </p>
        ) : (
          <p className={styles.hint}>Leave the weights empty to book the labelled quantity.</p>
        )}
        {check ? (
          <Checkbox label="Book the weighed amount as stock" checked={bookWeighed} onCheckedChange={setBookWeighed} />
        ) : null}
        <LocationSelect label="Put it in" value={locationId} onValueChange={setLocationId} optional />
        <TextField label="Supplier lot number" optional mono value={supplierLot} onValueChange={setSupplierLot} />
        <TextField label="Expires" optional type="date" value={expiresAt} onValueChange={setExpiresAt} />
        {errors.form ? <InlineAlert tone="danger" title={errors.form} /> : null}
      </form>
    </Dialog>
  );
}

/** Everything still on the order arrives: booked in at the labelled quantities on one date. */
export function ReceiveOrderDialog({
  orderId,
  waiting,
  open,
  onOpenChange,
  onReceived,
}: DialogBaseProps & { orderId: Uuid; waiting: number; onReceived?: (lotIds: Uuid[]) => void }) {
  const receive = useReceiveOrder(orderId);
  const toast = useToast();
  const [receivedAt, setReceivedAt] = useState(todayIso);
  const [locationId, setLocationId] = useState<Uuid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const count = pluralize(waiting, "lot", "lots");

  function submit(event?: FormEvent) {
    event?.preventDefault();
    receive.mutate(
      { received_at: receivedAt || null, location_id: locationId },
      {
        onSuccess: (order) => {
          toast.success(`${count} received`, {
            description: `${order.received_count} of ${order.lot_count} on this order are in stock.`,
          });
          onReceived?.(order.lots.filter((lot) => lot.status !== "on_order").map((lot) => lot.id));
          onOpenChange(false);
        },
        onError: (failure) => setError(describeError(failure)),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Receive ${count}`}
      description="Each container is booked in at its labelled quantity. To weigh a fill against its label, receive that container on its own first."
      footer={
        <>
          <DialogClose />
          <Button variant="primary" icon="check" onClick={() => submit()} pending={receive.isPending}>
            {`Receive ${count}`}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className={styles.form}>
        <TextField label="Received" type="date" value={receivedAt} onValueChange={setReceivedAt} />
        <LocationSelect label="Put them all in" value={locationId} onValueChange={setLocationId} optional />
        {error ? <InlineAlert tone="danger" title={error} /> : null}
      </form>
    </Dialog>
  );
}

/** A container that won't arrive (out of stock, refunded) comes off its order. */
export function RemoveFromOrderDialog({ lot, open, onOpenChange }: DialogBaseProps & { lot: OnOrderLot }) {
  const cancel = useCancelLot();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Remove lot ${lot.display_code} from the order?`}
      description={`For a container of ${lot.material.name} that won't arrive. It never held stock, so nothing else changes; the lot and its code are deleted.`}
      confirmLabel={`Remove ${lot.display_code} from the order`}
      cancelLabel="Keep it"
      pending={cancel.isPending}
      onConfirm={() =>
        cancel.mutate(
          { lotId: lot.id, reason },
          {
            onSuccess: () => {
              toast.success(`Lot ${lot.display_code} removed from the order`);
              onOpenChange(false);
            },
            onError: (failure) => setError(describeError(failure)),
          },
        )
      }
    >
      <div className={styles.form}>
        <TextField
          label="Reason"
          optional
          value={reason}
          onValueChange={setReason}
          placeholder="Out of stock, refunded…"
        />
        {error ? <InlineAlert tone="danger" title={error} /> : null}
      </div>
    </AlertDialog>
  );
}
